import React, { Component, setGlobal, Fragment } from "reactn";
import firebase from "js/firebase";
import logger from "js/core/logger";
import { TextInput } from "../components";
import { BlueSwitch } from "js/react/components/Switch.js";
import UploadAvatar from "../components/UploadAvatar";
import classNames from "classnames";
import { app } from "js/namespaces";
import { ds } from "js/core/models/dataService";
import Api from "js/core/api";
import {
    BlueButton,
    ScaryButton,
    Section,
    UIPane,
    UIPaneContents,
    UIPaneHeader
} from "js/react/components/UiComponents";
import { FlexBox } from "js/react/components/LayoutGrid";
import {
    ShowConfirmationDialog,
    ShowDialog,
    ShowErrorDialog,
    ShowMessageDialog
} from "js/react/components/Dialogs/BaseDialog";
import Loadable from "js/react/components/Loadable";
import LogOutButton from "js/react/components/LogOutButton";
import { withFirebaseUser } from "js/react/views/Auth/FirebaseUserContext";
import { withFirebaseAuth } from "js/react/views/Auth/FirebaseAuthContext";
import { withGoogleAuth } from "js/react/views/Auth/GoogleAuthContext";
import { ReauthPasswordDialog } from "js/react/views/UserOptions/dialogs/ReauthPasswordDialog";
import { ChangePasswordDialog } from "js/react/views/UserOptions/dialogs/ChangePasswordDialog";
import { ValidatedDeleteDialog } from "js/react/views/UserOptions/dialogs/ValidatedDeleteDialog";
import { CustomerType, SubscriptionStatus } from "common/constants";
import Spinner from "js/react/components/Spinner";
import gDrive from "js/core/utilities/gDrive";
import { trackActivity } from "js/core/utilities/utilities";
import { isElectron } from "js/config";
import { Gap20, Gap30 } from "js/react/components/Gap";

const authLog = logger.info(logger.AUTH).bind(logger);

function checkIfUserIsOnlyAdminInTeams(teamModels, userId) {
    return teamModels.reduce((acc, team) => {
        if (team.has("customerId")) {
            const members = Object.values(team.get("members"));
            let adminCount = members.filter(member => member.role === "admin").length;
            if (adminCount === 1 && team.get("members")[userId].role === "admin") {
                return true;
            }
        }
        return acc;
    }, false);
}

async function checkIfUserHasPastDueSubscription() {
    const subscription = await Api.subscriptions.get({ customer_type: CustomerType.INDIVIDUAL });
    return (subscription && subscription[0] && subscription[0].status === SubscriptionStatus.PAST_DUE);
}

function hasPasswordProvider(user) {
    return !!user.providerData.find(p => p.providerId === "password");
}

function hasGoogleProvider(user) {
    return !!user.providerData.find(p => p.providerId === "google.com");
}

class AccountPane extends Component {
    constructor(props) {
        super(props);
        const firebaseUser = props.firebaseUser;
        this.state = {
            avatarUrl: firebaseUser.photoURL,
            allowTracking: app.user.get("trackUsageData"),
            currentPane: "account",
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            emailVerified: firebaseUser.emailVerified,
            emailVerificationSent: false,
            emailVerificationStatusText: "Email is not verified - Click to send verification email",
            dialogMessage: null,
            name: firebaseUser.displayName || "",
            originalEmail: firebaseUser.email,
            previousEmail: firebaseUser.email,
            user: firebaseUser,
            userHasGoogleProvider: hasGoogleProvider(firebaseUser),
            userGoogleLinkFetching: false,
            hasProfile: true,
            userGDriveEnabled: app.user.get("isGDriveEnabled"),
            userGDriveFetching: false,
            canDeleteUser: Object.values(app.user.workspaces).length === 1,
            ssoStatus: null,
            fetching: true
        };
    }

    componentDidMount() {
        const { uid } = this.state;

        Api.ssoStatus.get({ uid })
            .then(({ status }) => this.setState({ fetching: false, ssoStatus: status }))
            .catch(err => {
                console.error(err);

                ShowErrorDialog({
                    error: "Uh oh, something isn't right",
                    message: "Looks like something unexpected has occurred. Please try again or contact us at support@beautiful.ai"
                });
            });
    }

    updateUserGoogleLinkState() {
        const user = this.props.firebaseUser;
        const userHasGoogleProvider = hasGoogleProvider(this.props.firebaseUser);
        this.setState({ user, userHasGoogleProvider });
    }

    setUserGDriveFetching(userGDriveFetching) {
        this.setState({ userGDriveFetching });
    }

    setUserGoogleLinkFetching(userGoogleLinkFetching) {
        this.setState({ userGoogleLinkFetching });
    }

    handleChange(name) {
        return event => {
            this.setState({ [name]: event.target.value });
        };
    }

    handleTrackingChange() {
        this.setState({ allowTracking: !this.state.allowTracking }, () => {
            app.user.update({ trackUsageData: this.state.allowTracking });
        });
    }

    handleChangePassword() {
        ShowDialog(ChangePasswordDialog);
    }

    handleDeleteAccount() {
        const { userGDriveEnabled } = this.state;

        ShowDialog(ValidatedDeleteDialog, {
            title: "Are you sure you want to delete your account?",
            message:
                "All your presentations and other data will be deleted from Beautiful.ai. You will not be able to undo this action.",
            prompt:
                "Please type your email address to confirm deletion of your account:",
            validateStr: app.user.getEmail(),
            callback: async isValidated => {
                if (isValidated) {
                    const userId = app.user.id;
                    const isPastDueFoundOnUser = await checkIfUserHasPastDueSubscription();
                    if (isPastDueFoundOnUser) {
                        ShowErrorDialog({
                            error: "Failed to delete user account",
                            message: "You cannot delete your account due to an outstanding payment on your subscription. " +
                                "Please email support@beautiful.ai for assistance."
                        });
                        return;
                    }
                    await ds.teams.loadModels();
                    if (checkIfUserIsOnlyAdminInTeams(ds.teams.models, userId)) {
                        ShowErrorDialog({
                            error: "Failed to delete user account",
                            message: "You cannot delete your account if you're the only admin of a pro team."
                        });
                        return;
                    }
                    if (userGDriveEnabled) {
                        await gDrive.disable();
                    }
                    ds.shutdown();
                    Api.deleteUser.delete().then(() => {
                        window.location = "/logout";
                    });
                }
            }
        });
    }

    async updateProfile(updates) {
        const { userMenu } = this.props;
        const { user } = this.state;
        await user
            .updateProfile(updates)
            .then(() => {
                if (updates.photoURL !== undefined) {
                    //userMenu is passed from the editor which still uses a backbone menu so we need to call updateAvatar
                    if (userMenu) {
                        userMenu.updateAvatar();
                    } else {
                        //else the profile is updated from the presentation library which is using react
                        setGlobal({ accountAvatarUrl: updates.photoURL });
                    }
                }
            })
            .catch(err => console.log(err));
    }

    async updateAvatar(obj) {
        console.log(obj.avatar);
        this.setState({
            avatarUrl: obj.avatar
        });
        await this.updateProfile({ photoURL: obj.avatar });
    }

    checkForVerifiedEmail() {
        app.user.waitForVerifiedEmail().then(() => {
            this.setState({
                emailVerified: true
            });
        });
    }

    async updateEmail() {
        const { email, originalEmail, previousEmail, user } = this.state;

        // No spam requests with same email over and over
        if (previousEmail === email) {
            return false;
        }

        app.user
            .updateEmailAddress(email)
            .then(() => {
                this.setState({
                    emailVerificationStatusText: "Verification email sent.",
                    previousEmail: email
                });
                ShowMessageDialog({
                    title: "Verification email sent.",
                    message:
                        "Please check your email for a verification link and return to this page after you click the link."
                });
                // this.checkForVerifiedEmail();
                app.mainView.renderVerificationBanner();
            })
            .catch(err => {
                switch (err.code) {
                    case "auth/requires-recent-login":
                        ShowDialog(ReauthPasswordDialog, {
                            callback: success => {
                                this.updateEmail();
                            },
                            onCancel: () => {
                                this.setState({
                                    email: originalEmail,
                                    previousEmail: null
                                });
                                ShowErrorDialog({
                                    error: "Unable to change email",
                                    message:
                                        "You must reauthenticate your account in order to change your email address."
                                });
                            }
                        });
                        break;
                    case "auth/invalid-email":
                    case "auth/email-already-in-use":
                    default:
                        this.setState({
                            email: originalEmail,
                            previousEmail: null
                        });
                        ShowErrorDialog({
                            error: "Error sending verification email.",
                            message: err.message
                        });
                        break;
                }
            });
    }

    renderVerifiedStatus(statusText) {
        const { emailVerified, emailVerificationSent, user } = this.state;
        if (user.emailVerified) {
            return null;
        } else {
            this.checkForVerifiedEmail();
            return (
                <div
                    className={classNames({
                        ["email-verification"]: true,
                        ["email-not-verified"]: true,
                        ["email-notice-button"]:
                            !emailVerificationSent && !emailVerified
                    })}
                    onClick={() => {
                        user.sendEmailVerification().then(() => {
                            this.setState({
                                emailVerificationStatusText:
                                    "Verification email sent",
                                emailVerificationSent: true
                            });
                        });
                    }}
                >
                    {statusText}
                </div>
            );
        }
    }

    getGDriveLink() {
        return (<a href="https://drive.google.com/drive" target="_blank">your Google Drive</a>);
    }

    handleLinkWithGoogle = async () => {
        this.setUserGoogleLinkFetching(true);
        const { auth2, firebaseUser } = this.props;

        try {
            // sign in to Google
            const googleUser = await auth2.signIn();
            authLog("Signed-in to Google as", googleUser);

            const response = googleUser.getAuthResponse();
            authLog("Google authResponse:", response);

            // link with Firebase
            const credential = firebase.auth.GoogleAuthProvider.credential(response.id_token);
            const userCredential = await firebaseUser.linkWithCredential(credential);
            authLog("Linked with Firebase, result:", userCredential);
        } catch (err) {
            if (err.code == "auth/provider-already-linked") {
                // already linked in Firebase Auth - shouldn't normally happen
                console.error(err);
            } else if (err.code == "auth/popup-closed-by-user") {
                // user closed the popup
                console.warn(err);
            } else if (err.code === "auth/credential-already-in-use" ||
                err.code === "auth/email-already-in-use") {
                ShowMessageDialog({
                    title: "Account exists",
                    message: "This Google account is linked with another user account, please sign in using Google. " +
                        "If you deleted a Beautiful account, you may need to sign in to Google an unlink your Beautiful account there.",
                    onClick: () => {
                    }
                });
            }
        }

        this.updateUserGoogleLinkState();
        this.setUserGoogleLinkFetching(false);
    }

    handleUnlinkGoogle = async () => {
        const userGDriveEnabled = this.state.userGDriveEnabled;
        ShowConfirmationDialog({
            title: "Disconnect from Google",
            message: `In order to disconnect, you must have a fallback password for login, otherwise you will have to reset your password via email.${userGDriveEnabled ? " Your Google Drive account will also be disconnected." : ""}`,
            acceptCallback: async () => {
                this.setUserGoogleLinkFetching(true);

                try {
                    if (userGDriveEnabled) {
                        this.setUserGDriveFetching(true);
                        try {
                            await gDrive.disable();
                        } catch (err) {
                            // fail gracefully if token is invalid
                            console.error(err);
                        }
                    }

                    const { auth, auth2, firebaseUser } = this.props;

                    authLog("Signing out of Google");
                    await auth2.signOut();

                    authLog("Unlinking Google account from Firebase");
                    await firebaseUser.unlink("google.com");

                    authLog("Deleting credentials from server");
                    await Api.unlinkGoogleAuth.delete();

                    authLog("Unlinking Google account successful");

                    if (!hasPasswordProvider(firebaseUser)) {
                        authLog("No password provider, sending password reset email");
                        await auth.sendPasswordResetEmail(firebaseUser.email);
                    }

                    this.updateUserGoogleLinkState();
                } catch (err) {
                    console.error(err);
                }

                if (userGDriveEnabled) {
                    this.setState({ userGDriveEnabled: false });
                    this.setUserGDriveFetching(false);
                }

                this.setUserGoogleLinkFetching(false);
            }
        });
    }

    currentUserGoogleEmail() {
        return (this.props.firebaseUser.providerData
            .find(p => p.providerId === "google.com") || {})
            .email;
    }

    render() {
        const {
            onClose,
        } = this.props;
        const {
            allowTracking,
            avatarUrl,
            email,
            emailVerificationStatusText,
            name,
            user,
            userHasGoogleProvider,
            hasProfile,
            userGoogleLinkFetching,
            userGDriveEnabled,
            userGDriveFetching,
            canDeleteUser,
            ssoStatus,
            fetching
        } = this.state;

        return (
            <UIPane>
                <UIPaneHeader onClose={onClose}>Account</UIPaneHeader>
                <UIPaneContents>
                    <Loadable isLoading={fetching}>
                        <Section title="Profile Settings">
                            <FlexBox row horizontalAlign="left" verticalAlign="top" style={{ display: app.isConstrained ? "block" : "flex" }}>
                                <div className="avatar-editor">
                                    <UploadAvatar
                                        src={avatarUrl || ""}
                                        label="Avatar"
                                        update={obj => this.updateAvatar(obj)}
                                        attribute="avatar"
                                        showSilhouette
                                    />
                                </div>
                                <div className="input-column account-info">
                                    <TextInput
                                        label="Name"
                                        id="user-name"
                                        curValue={name}
                                        handleChange={this.handleChange("name")}
                                        handleBlur={async () => {
                                            if (name.trim() !== "") {
                                                await this.updateProfile({
                                                    displayName: name
                                                });
                                            } else {
                                                ShowErrorDialog({
                                                    error:
                                                        "Unable to change display name",
                                                    message: `The display name can't be blank.`
                                                });
                                            }
                                        }}
                                    />
                                    <Gap20 />
                                    {hasProfile && <TextInput
                                        label="Email"
                                        classNames={["email-field"]}
                                        id="email"
                                        disabled={ssoStatus === "enabled" || ssoStatus === "strict"}
                                        curValue={email}
                                        handleChange={this.handleChange("email")}
                                        handleBlur={async () => {
                                            await this.updateEmail();
                                        }}
                                    >
                                        {this.renderVerifiedStatus(
                                            emailVerificationStatusText
                                        )}
                                    </TextInput>}
                                    <Gap20 />
                                    {hasProfile && hasPasswordProvider(user) && ssoStatus !== "strict" && (
                                        <BlueButton
                                            onClick={this.handleChangePassword}
                                        >
                                            Change Password...
                                        </BlueButton>
                                    )}
                                </div>
                            </FlexBox>
                        </Section>

                        {!app.isConstrained &&
                        <Fragment>
                            {hasProfile && <Section title="Account Linking">
                                {userHasGoogleProvider && (
                                    <div style={{ marginBottom: ".5em", fontSize: "1.1em" }}>
                                        {/* when the user account's email and Google email differ, show it */}
                                        {this.props.firebaseUser.email != this.currentUserGoogleEmail() && (
                                            <div className="gdrive-notice-text" style={{ marginBottom: ".75em" }}>
                                                Linked with <b>{this.currentUserGoogleEmail()}</b>
                                            </div>
                                        )}
                                        <BlueButton
                                            disabled={userGoogleLinkFetching}
                                            onClick={this.handleUnlinkGoogle}
                                        >
                                            {userGoogleLinkFetching && <Spinner />}
                                            <span>Disconnect Account From Google</span>
                                        </BlueButton>
                                    </div>
                                )}
                                {!userHasGoogleProvider && (
                                    <BlueButton
                                        disabled={userGoogleLinkFetching}
                                        onClick={this.handleLinkWithGoogle}
                                    >
                                        {userGoogleLinkFetching && <Spinner />}
                                        <span>Link Account With Google</span>
                                    </BlueButton>
                                )}
                            </Section>}

                            {!isElectron && (<Section title="Google Drive">
                                {userHasGoogleProvider && userGDriveEnabled &&
                                    <div className="gdrive-notice-text">
                                        Your presentations are synced with your Google Drive. You can find your presentations in {this.getGDriveLink()}.
                                    </div>}
                                {userHasGoogleProvider && !userGDriveEnabled &&
                                    <div className="gdrive-notice-text">
                                        Connect your Google Drive to have all your presentations available in your Google Drive.
                                    </div>}
                                {!userHasGoogleProvider &&
                                    <div className="gdrive-notice-text">
                                        Link your account with Google to sync your presentations with Google Drive.
                                    </div>}
                                {userHasGoogleProvider && userGDriveEnabled && <BlueButton
                                    disabled={userGDriveFetching}
                                    onClick={() => {
                                        this.setUserGDriveFetching(true);
                                        gDrive.disable()
                                            .then(() => {
                                                this.setState({ userGDriveEnabled: false }, () => {
                                                    ShowMessageDialog({
                                                        title: "Disconnect Google Drive",
                                                        message: <span>Your account was successfully disconnected from your Google Drive.</span>
                                                    });
                                                });
                                            })
                                            .catch(err => console.error(err))
                                            .finally(() => {
                                                this.setUserGDriveFetching(false);
                                            });
                                    }}
                                >
                                    {userGDriveFetching && <Spinner />}
                                    <span>Disconnect Google Drive</span>
                                </BlueButton>}
                                {userHasGoogleProvider && !userGDriveEnabled && <BlueButton
                                    disabled={userGDriveFetching}
                                    onClick={() => {
                                        trackActivity("GDrive", "ConnectCTAClick", null, null, {}, { audit: true });
                                        this.setUserGDriveFetching(true);
                                        gDrive.grantOfflineAccessWithDialogIfNeeded(this.props.auth2)
                                            .then(() => gDrive.enable())
                                            .then(() => {
                                                trackActivity("GDrive", "ConnectSuccess", null, null, {}, { audit: true });
                                                this.setState({ userGDriveEnabled: true }, () => {
                                                    ShowMessageDialog({
                                                        title: "Connect Google Drive",
                                                        message: <span>Your account was successfully connected with your Google Drive. You can find your presentations in {this.getGDriveLink()}.</span>
                                                    });
                                                });
                                            })
                                            .catch(err => {
                                                if (err.status === 409) {
                                                    ShowMessageDialog({
                                                        title: "Error",
                                                        message: <span>You must have selected a wrong account, your Google Drive account must match with the Google account you linked with Beautiful.ai</span>
                                                    });
                                                }
                                                console.error(err);
                                            })
                                            .finally(() => {
                                                this.setUserGDriveFetching(false);
                                            });
                                    }}
                                >
                                    {userGDriveFetching && <Spinner />}
                                    <span>Connect Google Drive</span>
                                </BlueButton>}
                            </Section>)}

                            <Section title="Privacy Settings">
                                <div className="content-wrapper">
                                    <div className="input-column privacy-notice">
                                        <div className="privacy-notice-text">
                                            For internal use only, we may track and record
                                            data about how you use Beautiful.ai
                                            in order to help us improve our products and diagnose issues.
                                            Review of that information may expose your
                                            presentation contents to certain authorized Beautiful.AI employees.
                                        </div>
                                        <BlueSwitch
                                            checked={allowTracking}
                                            onChange={() =>
                                                this.handleTrackingChange(
                                                    "allowTracking"
                                                )
                                            }
                                            label="Allow Usage Tracking"
                                        />
                                    </div>
                                </div>
                            </Section>

                            {hasProfile && canDeleteUser && <Section title="Account Deletion">
                                <ScaryButton onClick={() => this.handleDeleteAccount()}>
                                    Delete My Account...
                                </ScaryButton>
                            </Section>}
                        </Fragment>}
                    </Loadable>
                    {
                        app.isConstrained &&
                        <div style={{ marginTop: "20px" }}>
                            <FlexBox center>
                                <LogOutButton/>
                            </FlexBox>
                            <Gap30/>
                        </div>
                    }
                </UIPaneContents>
            </UIPane>
        );
    }
}

export default withGoogleAuth(withFirebaseAuth(withFirebaseUser(AccountPane)));
